import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./EmiCalculator.css";
import Seo from "../components/Seo";

const loanTypes = {
  home: {
    label: "Home Loan",
    icon: "bi bi-house-door",
    defaultAmount: 5000000,
    defaultRate: 8.5,
    defaultTenure: 20,
    minAmount: 500000,
    maxAmount: 50000000,
    amountStep: 100000,
    minRate: 5,
    maxRate: 15,
    minTenure: 1,
    maxTenure: 30,
  },

  personal: {
    label: "Personal Loan",
    icon: "bi bi-person-check",
    defaultAmount: 1000000,
    defaultRate: 11.5,
    defaultTenure: 5,
    minAmount: 50000,
    maxAmount: 5000000,
    amountStep: 50000,
    minRate: 8,
    maxRate: 30,
    minTenure: 1,
    maxTenure: 7,
  },

  business: {
    label: "Business Loan",
    icon: "bi bi-briefcase",
    defaultAmount: 2500000,
    defaultRate: 12,
    defaultTenure: 5,
    minAmount: 100000,
    maxAmount: 25000000,
    amountStep: 100000,
    minRate: 8,
    maxRate: 30,
    minTenure: 1,
    maxTenure: 10,
  },

  car: {
    label: "Car Loan",
    icon: "bi bi-car-front",
    defaultAmount: 1200000,
    defaultRate: 8.75,
    defaultTenure: 7,
    minAmount: 100000,
    maxAmount: 10000000,
    amountStep: 50000,
    minRate: 5,
    maxRate: 20,
    minTenure: 1,
    maxTenure: 8,
  },

  education: {
    label: "Education Loan",
    icon: "bi bi-mortarboard",
    defaultAmount: 2000000,
    defaultRate: 9,
    defaultTenure: 10,
    minAmount: 100000,
    maxAmount: 20000000,
    amountStep: 100000,
    minRate: 5,
    maxRate: 20,
    minTenure: 1,
    maxTenure: 15,
  },

  lap: {
    label: "Loan Against Property",
    icon: "bi bi-buildings",
    defaultAmount: 7500000,
    defaultRate: 9.25,
    defaultTenure: 15,
    minAmount: 1000000,
    maxAmount: 100000000,
    amountStep: 500000,
    minRate: 6,
    maxRate: 20,
    minTenure: 1,
    maxTenure: 25,
  },
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);

const clampValue = (value, minimum, maximum) =>
  Math.min(Math.max(Number(value) || minimum, minimum), maximum);

const EmiCalculator = () => {
  const [selectedLoanType, setSelectedLoanType] = useState("home");
  const [loanAmount, setLoanAmount] = useState(
    loanTypes.home.defaultAmount
  );
  const [interestRate, setInterestRate] = useState(
    loanTypes.home.defaultRate
  );
  const [loanTenure, setLoanTenure] = useState(
    loanTypes.home.defaultTenure
  );

  const activeLoan = loanTypes[selectedLoanType];

  useEffect(() => {
    setLoanAmount(activeLoan.defaultAmount);
    setInterestRate(activeLoan.defaultRate);
    setLoanTenure(activeLoan.defaultTenure);
  }, [selectedLoanType, activeLoan]);

  const calculation = useMemo(() => {
    const principal = Number(loanAmount);
    const annualInterestRate = Number(interestRate);
    const tenureYears = Number(loanTenure);
    const numberOfMonths = tenureYears * 12;
    const monthlyInterestRate = annualInterestRate / 100 / 12;

    if (
      !principal ||
      principal <= 0 ||
      !numberOfMonths ||
      numberOfMonths <= 0
    ) {
      return {
        emi: 0,
        totalInterest: 0,
        totalPayment: 0,
        principalPercentage: 0,
        interestPercentage: 0,
      };
    }

    let calculatedEmi = 0;

    if (monthlyInterestRate === 0) {
      calculatedEmi = principal / numberOfMonths;
    } else {
      const growthFactor = Math.pow(
        1 + monthlyInterestRate,
        numberOfMonths
      );

      calculatedEmi =
        (principal * monthlyInterestRate * growthFactor) /
        (growthFactor - 1);
    }

    const calculatedTotalPayment =
      calculatedEmi * numberOfMonths;

    const calculatedTotalInterest =
      calculatedTotalPayment - principal;

    const principalPercentage =
      calculatedTotalPayment > 0
        ? (principal / calculatedTotalPayment) * 100
        : 0;

    const interestPercentage =
      calculatedTotalPayment > 0
        ? (calculatedTotalInterest / calculatedTotalPayment) * 100
        : 0;

    return {
      emi: Math.round(calculatedEmi),
      totalInterest: Math.round(calculatedTotalInterest),
      totalPayment: Math.round(calculatedTotalPayment),
      principalPercentage,
      interestPercentage,
    };
  }, [loanAmount, interestRate, loanTenure]);

  const amountSliderProgress =
    ((loanAmount - activeLoan.minAmount) /
      (activeLoan.maxAmount - activeLoan.minAmount)) *
    100;

  const rateSliderProgress =
    ((interestRate - activeLoan.minRate) /
      (activeLoan.maxRate - activeLoan.minRate)) *
    100;

  const tenureSliderProgress =
    ((loanTenure - activeLoan.minTenure) /
      (activeLoan.maxTenure - activeLoan.minTenure)) *
    100;

  const handleAmountChange = (value) => {
    setLoanAmount(
      clampValue(
        value,
        activeLoan.minAmount,
        activeLoan.maxAmount
      )
    );
  };

  const handleInterestChange = (value) => {
    setInterestRate(
      clampValue(
        value,
        activeLoan.minRate,
        activeLoan.maxRate
      )
    );
  };

  const handleTenureChange = (value) => {
    setLoanTenure(
      clampValue(
        value,
        activeLoan.minTenure,
        activeLoan.maxTenure
      )
    );
  };

  return (
    <>
      <Seo
        title="Loan EMI Calculator | MONAYRA (OPC) PRIVATE LIMITED"
        description="Use the MONAYRA EMI calculator to estimate monthly repayments for home loans, personal loans, business loans, car loans, education loans and Loan Against Property."
        keywords="EMI calculator, loan EMI calculator, home loan EMI, personal loan EMI, car loan EMI, business loan EMI, education loan EMI"
        canonical="https://www.mortbuzz.in/emi-calculator"
      />

      <main className="monayra-emi-page">
        <section className="emi-page-hero">
          <div className="emi-page-hero-overlay"></div>

          <div
            className="emi-page-glow emi-page-glow-left"
            aria-hidden="true"
          ></div>

          <div
            className="emi-page-glow emi-page-glow-right"
            aria-hidden="true"
          ></div>

          <div className="emi-page-container emi-page-hero-grid">
            <div className="emi-page-hero-content">
              <span className="emi-page-eyebrow">
                Loan Repayment Planner
              </span>

              <h1>
                Calculate your loan EMI and
                <span> plan with confidence</span>
              </h1>

              <p>
                Estimate your monthly EMI, total interest and total
                repayment for different loan products before submitting
                your application.
              </p>

              <div className="emi-page-hero-features">
                <div>
                  <i className="bi bi-calculator"></i>
                  Instant EMI estimate
                </div>

                <div>
                  <i className="bi bi-sliders"></i>
                  Flexible loan inputs
                </div>

                <div>
                  <i className="bi bi-pie-chart"></i>
                  Repayment breakdown
                </div>
              </div>
            </div>

            <aside className="emi-page-hero-card">
              <span>
                <i className="bi bi-lightning-charge"></i>
              </span>

              <small>Estimated Monthly EMI</small>

              <strong>{formatCurrency(calculation.emi)}</strong>

              <p>
                Based on {formatCurrency(loanAmount)} at{" "}
                {interestRate}% for {loanTenure} years.
              </p>

              <Link
                to="/apply-now"
                className="emi-page-primary-btn"
              >
                Apply for a Loan
                <i className="bi bi-arrow-right"></i>
              </Link>
            </aside>
          </div>
        </section>

        <section className="emi-calculator-section">
          <div className="emi-page-container">
            <div className="emi-page-section-header">
              <span className="emi-page-eyebrow">
                EMI Calculator
              </span>

              <h2>
                Customise your
                <span> loan details</span>
              </h2>

              <p>
                Select a loan type and adjust the amount, interest rate
                and repayment tenure.
              </p>
            </div>

            <div className="emi-loan-type-grid">
              {Object.entries(loanTypes).map(
                ([loanKey, loanDetails]) => (
                  <button
                    type="button"
                    key={loanKey}
                    className={`emi-loan-type-btn ${
                      selectedLoanType === loanKey
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedLoanType(loanKey)
                    }
                  >
                    <span>
                      <i className={loanDetails.icon}></i>
                    </span>

                    {loanDetails.label}
                  </button>
                )
              )}
            </div>

            <div className="emi-calculator-layout">
              <section className="emi-input-panel">
                <div className="emi-panel-heading">
                  <div>
                    <span>Loan Details</span>
                    <h3>{activeLoan.label} Calculator</h3>
                  </div>

                  <span className="emi-selected-loan-icon">
                    <i className={activeLoan.icon}></i>
                  </span>
                </div>

                <div className="emi-input-group">
                  <div className="emi-input-heading">
                    <label htmlFor="emiLoanAmount">
                      Loan Amount
                    </label>

                    <div className="emi-number-input">
                      <span>₹</span>

                      <input
                        id="emiLoanAmount"
                        type="number"
                        min={activeLoan.minAmount}
                        max={activeLoan.maxAmount}
                        step={activeLoan.amountStep}
                        value={loanAmount}
                        onChange={(event) =>
                          handleAmountChange(event.target.value)
                        }
                        aria-label="Loan amount"
                      />
                    </div>
                  </div>

                  <input
                    type="range"
                    className="emi-range-input"
                    min={activeLoan.minAmount}
                    max={activeLoan.maxAmount}
                    step={activeLoan.amountStep}
                    value={loanAmount}
                    onChange={(event) =>
                      setLoanAmount(Number(event.target.value))
                    }
                    style={{
                      "--emi-progress": `${amountSliderProgress}%`,
                    }}
                    aria-label="Loan amount slider"
                  />

                  <div className="emi-range-labels">
                    <span>{formatCurrency(activeLoan.minAmount)}</span>
                    <span>{formatCurrency(activeLoan.maxAmount)}</span>
                  </div>
                </div>

                <div className="emi-input-group">
                  <div className="emi-input-heading">
                    <label htmlFor="emiInterestRate">
                      Interest Rate
                    </label>

                    <div className="emi-number-input">
                      <input
                        id="emiInterestRate"
                        type="number"
                        min={activeLoan.minRate}
                        max={activeLoan.maxRate}
                        step="0.1"
                        value={interestRate}
                        onChange={(event) =>
                          handleInterestChange(event.target.value)
                        }
                        aria-label="Annual interest rate"
                      />

                      <span>%</span>
                    </div>
                  </div>

                  <input
                    type="range"
                    className="emi-range-input"
                    min={activeLoan.minRate}
                    max={activeLoan.maxRate}
                    step="0.1"
                    value={interestRate}
                    onChange={(event) =>
                      setInterestRate(Number(event.target.value))
                    }
                    style={{
                      "--emi-progress": `${rateSliderProgress}%`,
                    }}
                    aria-label="Interest rate slider"
                  />

                  <div className="emi-range-labels">
                    <span>{activeLoan.minRate}%</span>
                    <span>{activeLoan.maxRate}%</span>
                  </div>
                </div>

                <div className="emi-input-group">
                  <div className="emi-input-heading">
                    <label htmlFor="emiLoanTenure">
                      Loan Tenure
                    </label>

                    <div className="emi-number-input">
                      <input
                        id="emiLoanTenure"
                        type="number"
                        min={activeLoan.minTenure}
                        max={activeLoan.maxTenure}
                        step="1"
                        value={loanTenure}
                        onChange={(event) =>
                          handleTenureChange(event.target.value)
                        }
                        aria-label="Loan tenure in years"
                      />

                      <span>
                        {loanTenure === 1 ? "Year" : "Years"}
                      </span>
                    </div>
                  </div>

                  <input
                    type="range"
                    className="emi-range-input"
                    min={activeLoan.minTenure}
                    max={activeLoan.maxTenure}
                    step="1"
                    value={loanTenure}
                    onChange={(event) =>
                      setLoanTenure(Number(event.target.value))
                    }
                    style={{
                      "--emi-progress": `${tenureSliderProgress}%`,
                    }}
                    aria-label="Loan tenure slider"
                  />

                  <div className="emi-range-labels">
                    <span>{activeLoan.minTenure} year</span>
                    <span>{activeLoan.maxTenure} years</span>
                  </div>
                </div>

                <div className="emi-input-note">
                  <i className="bi bi-info-circle"></i>

                  <p>
                    This calculator provides an indicative estimate.
                    Actual lender calculations may vary based on rate
                    type, processing method and repayment schedule.
                  </p>
                </div>
              </section>

              <section className="emi-result-panel">
                <div className="emi-result-heading">
                  <span>Repayment Summary</span>
                  <h3>Your estimated EMI</h3>
                </div>

                <div className="emi-main-result">
                  <small>Monthly EMI</small>
                  <strong>{formatCurrency(calculation.emi)}</strong>

                  <p>
                    For {loanTenure * 12} monthly instalments
                  </p>
                </div>

                <div className="emi-summary-grid">
                  <article>
                    <span>
                      <i className="bi bi-cash-stack"></i>
                    </span>

                    <div>
                      <small>Principal Amount</small>
                      <strong>{formatCurrency(loanAmount)}</strong>
                    </div>
                  </article>

                  <article>
                    <span>
                      <i className="bi bi-percent"></i>
                    </span>

                    <div>
                      <small>Total Interest</small>
                      <strong>
                        {formatCurrency(calculation.totalInterest)}
                      </strong>
                    </div>
                  </article>

                  <article>
                    <span>
                      <i className="bi bi-wallet2"></i>
                    </span>

                    <div>
                      <small>Total Payment</small>
                      <strong>
                        {formatCurrency(calculation.totalPayment)}
                      </strong>
                    </div>
                  </article>

                  <article>
                    <span>
                      <i className="bi bi-calendar3"></i>
                    </span>

                    <div>
                      <small>Loan Period</small>
                      <strong>
                        {loanTenure}{" "}
                        {loanTenure === 1 ? "Year" : "Years"}
                      </strong>
                    </div>
                  </article>
                </div>

                <div className="emi-breakdown-card">
                  <div className="emi-breakdown-heading">
                    <span>Payment Breakdown</span>
                    <small>Total repayment distribution</small>
                  </div>

                  <div
                    className="emi-breakdown-bar"
                    aria-label="Principal and interest payment breakdown"
                  >
                    <span
                      className="emi-principal-bar"
                      style={{
                        width: `${calculation.principalPercentage}%`,
                      }}
                    ></span>

                    <span
                      className="emi-interest-bar"
                      style={{
                        width: `${calculation.interestPercentage}%`,
                      }}
                    ></span>
                  </div>

                  <div className="emi-breakdown-legend">
                    <div>
                      <span className="emi-principal-dot"></span>

                      <div>
                        <small>Principal</small>
                        <strong>
                          {calculation.principalPercentage.toFixed(1)}%
                        </strong>
                      </div>
                    </div>

                    <div>
                      <span className="emi-interest-dot"></span>

                      <div>
                        <small>Interest</small>
                        <strong>
                          {calculation.interestPercentage.toFixed(1)}%
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <Link
                  to="/apply-now"
                  className="emi-page-primary-btn emi-result-apply-btn"
                >
                  Apply for {activeLoan.label}
                  <i className="bi bi-arrow-right"></i>
                </Link>
              </section>
            </div>
          </div>
        </section>

        <section className="emi-information-section">
          <div className="emi-page-container emi-information-grid">
            <div className="emi-information-content">
              <span className="emi-page-eyebrow">
                Understanding EMI
              </span>

              <h2>
                What does your
                <span> EMI include?</span>
              </h2>

              <p>
                EMI stands for Equated Monthly Instalment. It is the
                estimated fixed monthly amount paid towards repayment of
                your loan over the selected tenure.
              </p>

              <p>
                Each EMI generally includes a principal component and an
                interest component. During the earlier part of the loan,
                the interest component may be higher.
              </p>

              <div className="emi-information-points">
                <div>
                  <span>
                    <i className="bi bi-cash-coin"></i>
                  </span>

                  <div>
                    <strong>Principal</strong>
                    <small>
                      The original amount borrowed from the lender.
                    </small>
                  </div>
                </div>

                <div>
                  <span>
                    <i className="bi bi-percent"></i>
                  </span>

                  <div>
                    <strong>Interest</strong>
                    <small>
                      The cost charged by the lender for providing the
                      loan.
                    </small>
                  </div>
                </div>

                <div>
                  <span>
                    <i className="bi bi-calendar-check"></i>
                  </span>

                  <div>
                    <strong>Tenure</strong>
                    <small>
                      The total duration selected for repaying the loan.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <aside className="emi-formula-card">
              <span className="emi-formula-icon">
                <i className="bi bi-calculator"></i>
              </span>

              <span className="emi-formula-eyebrow">
                EMI Formula
              </span>

              <h3>How EMI is calculated</h3>

              <div className="emi-formula-expression">
                EMI = P × R × (1 + R)<sup>N</sup>
                <hr />
                (1 + R)<sup>N</sup> − 1
              </div>

              <ul>
                <li>
                  <strong>P</strong>
                  <span>Principal loan amount</span>
                </li>

                <li>
                  <strong>R</strong>
                  <span>Monthly interest rate</span>
                </li>

                <li>
                  <strong>N</strong>
                  <span>Total number of monthly instalments</span>
                </li>
              </ul>
            </aside>
          </div>
        </section>

        <section className="emi-cta-section">
          <div className="emi-page-container">
            <div className="emi-cta-card">
              <div>
                <span>Ready to Continue?</span>

                <h2>Explore suitable loan options with MONAYRA</h2>

                <p>
                  Share your requirement and receive assistance with
                  eligibility, documentation and lender comparison.
                </p>
              </div>

              <div className="emi-cta-actions">
                <Link
                  to="/apply-now"
                  className="emi-page-primary-btn"
                >
                  Apply Now
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/contact"
                  className="emi-page-secondary-btn"
                >
                  <i className="bi bi-headset"></i>
                  Contact Our Team
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="emi-page-disclaimer">
          <div className="emi-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              The EMI values shown are indicative and based on the
              information entered. Actual EMI, interest amount, repayment
              schedule, charges and approval terms may vary according to
              the selected lender. MONAYRA (OPC) PRIVATE LIMITED provides
              loan assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
};

export default EmiCalculator;